import { Button } from '@thanx/uikit/button'
import { Checkbox } from '@thanx/uikit/checkbox'
import { Text } from '@thanx/uikit/text'
import type { MerchantUser } from 'api/merchantUsers'
import Badge from 'components/Badge'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { displayName } from 'scenes/RecoveryAI/components/merchantUserName'
import { useStyletron } from 'styletron-react'

const t = buildTranslate('recovery_ai.settings.escalation_contacts')

// The Team / merchant-users management route (scenes/MerchantUsers) — NOT
// `/users` (scenes/Users, the end-customer list). Deep-linked from the invite
// affordance. The `?tab=users` is required, not decorative: when
// role-based-access-control is on, this route mounts scenes/DashboardAccess,
// which defaults `activeTab` to 'roles' whenever the `tab` query param is
// absent/invalid — without it, an RBAC merchant following this link would
// land on the Roles tab instead of the Users tab the invite affordance means
// to point them at.
const MERCHANT_USERS_PATH = '/merchant_users?tab=users'

const AVATAR_SIZE = '32px'
const FIELD_RADIUS = '8px'
const PANEL_MAX_HEIGHT = '288px'
// Floats the open panel above the following page content (matches the repo's
// in-page dropdown z-index precedent). Exported so the wizard sticky-bar
// ordering invariant test can assert the bar stays above it (AI-1123).
export const PANEL_Z_INDEX = 10

// id linking the optional description to the combobox trigger via
// aria-describedby. Namespaced per instance rather than a module constant: a
// Settings pane and an open config modal can hold description-bearing pickers at
// the same time, and a duplicate id makes both triggers describe whichever
// element comes first in the DOM. React 16 has no useId, and a generated id would
// break the document.getElementById assertions this repo relies on.
const descriptionId = (instanceId: string) =>
  `recipient-picker-description-${instanceId}`

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase() || '?'

// Circular initials avatar (Figma shows a tinted circle, not the silhouette in
// components/Avatar). Kept local + prop-driven.
const InitialsAvatar = ({ name }: { name: string }) => {
  const [css] = useStyletron()
  return (
    <div
      aria-hidden="true"
      className={`belize-hole-10-bkg belize-hole-50 ${css({
        width: AVATAR_SIZE,
        height: AVATAR_SIZE,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontWeight: 'bold',
        fontSize: '12px',
      })}`}
    >
      {initialsOf(name)}
    </div>
  )
}

// Avatar + bold name over email — shared by the single-mode trigger and the
// multi-mode selected rows.
//
//   AI-1284: `badgeLabel`, when passed, renders a badge immediately after the
//   name (design: the collapsed single-mode trigger's "ACCOUNT ADMIN" badge).
//   Only the single-mode trigger call site passes it — the multi-mode
//   selected-row call site never does, so those rows are unaffected
//   regardless of the picker's own showAdminBadge prop.
const RecipientIdentity = ({
  user,
  badgeLabel,
}: {
  user: MerchantUser
  badgeLabel?: string
}) => {
  const [css] = useStyletron()
  return (
    <div
      className={css({ display: 'flex', alignItems: 'center', minWidth: 0 })}
    >
      <InitialsAvatar name={displayName(user)} />
      <div
        className={`ml-s ${css({ display: 'flex', flexDirection: 'column' })}`}
      >
        <div className={css({ display: 'flex', alignItems: 'center' })}>
          <Text.BodyText3 bold>{displayName(user)}</Text.BodyText3>
          {/* Grey, not the dropdown's belize blue: the design draws this one
              as a neutral pill (grey70 on grey10). `grey-70 grey-*-bkg` is an
              existing Badge pairing in this repo, so this is still one of the
              two shipped badge looks rather than a third, hand-rolled one. */}
          {badgeLabel && (
            <Badge className="grey-70 grey-10-bkg ml-xs" value={badgeLabel} />
          )}
        </div>
        <Text.BodyText3 color="grey50">{user.email}</Text.BodyText3>
      </div>
    </div>
  )
}

type Mode = 'single' | 'multi'

/**
 * How the "can't find the right person? Invite someone" affordance behaves.
 *
 *   context:
 *   - 'link'     same-tab <Link> to the Team page. Today's behaviour; the
 *                Settings tab keeps it.
 *   - 'new-tab'  the same router <Link> with target="_blank". The wizard passes
 *                this because a same-tab nav unmounts WizardProvider and discards
 *                the in-progress onboarding state on the last step.
 *   - 'hidden'   omitted entirely. The Activation modal passes this: the nav would
 *                discard the staged selection with no warning.
 */
export type InviteAffordance = 'link' | 'new-tab' | 'hidden'

export type RecipientPickerProps = {
  /**
   * REQUIRED (never optional-with-default): namespaces this instance's
   * description id so a second concurrent mount cannot collide with it. A
   * default would reintroduce the collision at whichever call site the author
   * forgot. e.g. 'settings-primary' / 'activation-modal-additional'.
   */
  instanceId: string
  users: MerchantUser[]
  value: number[]
  onChange: (ids: number[]) => void
  // `single` = the primary/fallback recipient (at most one id); `multi` = the
  // optional additional recipients.
  mode: Mode
  label: string
  // Optional paragraph rendered under the label (label → description → field),
  // e.g. the additional-recipients pane. Omitted → label sits on the field.
  description?: string
  /**
   * AI-1284: hides the visible `label` block above the field, for the Order
   * feedback primary picker (its label is redundant with the section
   * heading above it). `label` stays REQUIRED even when this is true — it is
   * still the field's accessible name via `aria-label` on the trigger, so the
   * control is never unnamed. Do not pass `label=""` to hide it; that leaves
   * the trigger with no accessible name at all.
   */
  hideLabel?: boolean
  disabled?: boolean
  // Rendered only under the single/primary picker, as today. Defaults to the
  // shipped same-tab link so existing consumers are unaffected.
  inviteAffordance?: InviteAffordance
  /**
   * AI-1284: drops the trailing `fa-external-link` icon from the invite CTA,
   * for the Order feedback primary picker's plain-text "Invite someone" link.
   * Independent of `inviteAffordance` — the new-tab sr-only announcement is
   * unaffected either way. Defaults to true (today's icon-on-every-caller
   * behaviour).
   */
  showInviteIcon?: boolean
  /**
   * AI-1284: opt-in badge on the collapsed SINGLE-mode trigger row, shown
   * immediately after the selected recipient's name when `isAdmin` returns
   * true for them. Defaults to false so every existing caller
   * (EscalationContactsBody's three RecoveryAI surfaces) is unchanged. The
   * multi-mode picker never renders it, regardless of this flag.
   */
  showAdminBadge?: boolean
  /**
   * The badge's resolved text, rendered only when `showAdminBadge` is true.
   * Passed in by the caller rather than read from a `t()` call inside this
   * shared component, so RecipientPicker never has to reach into an
   * Order-feedback-specific locale namespace. Ignored when `showAdminBadge`
   * is false.
   */
  adminBadgeLabel?: string
  /**
   * AI-1284: links the trigger to an external note explaining why it is
   * disabled (the Order feedback Notifications pane's written-feedback gate),
   * via aria-describedby. Combined with the picker's OWN `description` id
   * when both are present, rather than one replacing the other. Optional and
   * undefined by default, so every other call site is unaffected.
   */
  ariaDescribedBy?: string
  // Required, and injected so the picker stays fetch-free. No default: the old
  // `role === Role.admin` fallback read the dead legacy column and silently
  // mis-grouped RBAC admins into ALL OTHER USERS.
  //
  //   context: #3883 resolved this in the PANE, which was then the picker's only
  //   consumer. The picker now has three (Settings, the activation modal, the
  //   wizard step) reached through one shared body, so it is resolved once in
  //   useEscalationContactsController — the data layer all three already share —
  //   rather than three times. Still injected, so this stays presentational.
  isAdmin: (user: MerchantUser) => boolean
}

// Presentational recipient picker (AI-32). Selects EXISTING merchant users
// (never free-typed emails — AI-4/D2) via a collapsed dropdown: the trigger
// shows the selected recipient (single) or an "<n> recipients selected" summary
// (multi); opening it reveals the grouped ADMINS / ALL OTHER USERS list with an
// ADMIN badge. Multi selections render below as removable avatar rows.
// Controlled + prop-driven — no data fetch: the host supplies `users` and injects
// the admin predicate, because resolving it RBAC-aware needs the roles query.
const RecipientPicker = ({
  instanceId,
  users,
  value,
  onChange,
  mode,
  label,
  description,
  hideLabel = false,
  disabled,
  inviteAffordance = 'link',
  showInviteIcon = true,
  showAdminBadge = false,
  adminBadgeLabel,
  ariaDescribedBy,
  isAdmin,
}: RecipientPickerProps) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const descId = descriptionId(instanceId)
  // Combines the picker's own optional description id with a caller-supplied
  // one (e.g. a written-feedback gate note) rather than one clobbering the
  // other — the additional-recipients picker already carries its own
  // description, and the gate note has to reach the trigger alongside it.
  const describedBy =
    [description ? descId : undefined, ariaDescribedBy]
      .filter(Boolean)
      .join(' ') || undefined
  const showInvite = mode === 'single' && inviteAffordance !== 'hidden'
  const opensInNewTab = inviteAffordance === 'new-tab'
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const admins = users.filter(isAdmin)
  const others = users.filter(user => !isAdmin(user))

  // Resolve selected ids → users (skipping any id no longer in the list).
  const selectedUsers = value
    .map(id => users.find(user => user.id === id))
    .filter((user): user is MerchantUser => Boolean(user))

  // While open: dismiss on an outside click or Escape — and on Escape return
  // focus to the trigger (the combobox-dismissal a11y pattern). The trigger
  // toggles open/closed directly. Listeners are document-level (not a JSX
  // handler on a non-interactive wrapper) and only attached while open.
  useEffect(() => {
    if (!open) return undefined
    const onPointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleToggle = (id: number, checked: boolean) => {
    if (mode === 'single') {
      // Single mode holds at most one id — selecting replaces, then collapses.
      onChange(checked ? [id] : [])
      setOpen(false)
      return
    }
    onChange(
      checked ? [...value, id] : value.filter(existing => existing !== id)
    )
  }

  const handleRemove = (id: number) =>
    onChange(value.filter(existing => existing !== id))

  const renderGroup = (
    groupLabel: string,
    groupUsers: MerchantUser[],
    showAdminBadge: boolean
  ) => {
    if (groupUsers.length === 0) return null
    return (
      // role="group" + the group label as the accessible name so membership is
      // programmatically scopeable, not just visually present.
      <div role="group" aria-label={groupLabel} className="px-m py-s">
        <Text.BodyText3 bold color="grey50">
          {groupLabel}
        </Text.BodyText3>
        {groupUsers.map(user => (
          <div
            key={user.id}
            className={css({
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
          >
            <Checkbox
              checked={value.includes(user.id)}
              onChange={checked => handleToggle(user.id, checked)}
              disabled={disabled}
            >
              {displayName(user)}
            </Checkbox>
            <div className={css({ display: 'flex', alignItems: 'center' })}>
              <Text.BodyText3 color="grey50" className="mr-xs">
                {user.email}
              </Text.BodyText3>
              {showAdminBadge && (
                <Badge
                  className="belize-hole-50 belize-hole-10-bkg"
                  value={t('admin_badge')}
                />
              )}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderTriggerContent = () => {
    if (mode === 'single') {
      const selected = selectedUsers[0]
      if (!selected) {
        return (
          <Text.BodyText3 color="grey50">
            {t('select_recipient_placeholder')}
          </Text.BodyText3>
        )
      }
      const badgeLabel =
        showAdminBadge && isAdmin(selected) ? adminBadgeLabel : undefined
      return <RecipientIdentity user={selected} badgeLabel={badgeLabel} />
    }
    return (
      <Text.BodyText3>
        {/* aria-live so screen readers hear the count change as recipients are
            checked/unchecked in the open panel (WCAG 4.1.3). */}
        <span aria-live="polite">
          {/* Count the RESOLVED users (selectedUsers), not raw ids — a stale id
              with no matching user renders no row, so value.length would
              over-count vs what's shown. */}
          {t('recipients_selected', { count: selectedUsers.length })}
        </span>
      </Text.BodyText3>
    )
  }

  return (
    <div ref={containerRef}>
      {!hideLabel && <Text.BodyText3 bold>{label}</Text.BodyText3>}

      {description && (
        // Optional description under the label (Figma order: label → desc →
        // field). BodyText3 is an inline span, so the top gap (mt-xxs = 4px)
        // rides a block wrapper, which also carries the id the trigger's
        // aria-describedby points at.
        <div id={descId} className="mt-xxs">
          <Text.BodyText3 color="grey70">{description}</Text.BodyText3>
        </div>
      )}

      {/* position:relative anchor so the panel floats as an absolute overlay
          instead of pushing the rest of the pane down. */}
      <div className={`mt-xxs ${css({ position: 'relative' })}`}>
        {/* Select-field trigger. A uikit Button can't express a full-width field
            surface with arbitrary content + a trailing caret, so a native button
            with full combobox a11y is used here (documented exemption). */}
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={label}
          aria-describedby={describedBy}
          disabled={disabled}
          onClick={() => setOpen(prev => !prev)}
          className={`bg-white ${css({
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${palette.grey20}`,
            borderRadius: FIELD_RADIUS,
            padding: '12px 16px',
            cursor: disabled ? 'not-allowed' : 'pointer',
            textAlign: 'left',
          })}`}
        >
          {renderTriggerContent()}
          <i className="fa fa-caret-down" aria-hidden="true" />
        </button>

        {open && (
          <div
            className={`bg-white ${css({
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: PANEL_Z_INDEX,
              border: `1px solid ${palette.grey20}`,
              borderRadius: FIELD_RADIUS,
              marginTop: '4px',
              maxHeight: PANEL_MAX_HEIGHT,
              overflowY: 'auto',
              // Elevation so the floating panel reads above the page content
              // (rgba shadow — palette has no alpha token; matches repo precedent).
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
            })}`}
          >
            {renderGroup(t('group_admins'), admins, true)}
            {renderGroup(t('group_others'), others, false)}
          </div>
        )}
      </div>

      {mode === 'multi' && selectedUsers.length > 0 && (
        <ul
          aria-label={t('selected_recipients_label')}
          className={css({ listStyle: 'none', padding: 0, margin: '8px 0 0' })}
        >
          {selectedUsers.map(user => (
            <li
              key={user.id}
              className={`bg-white ${css({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid ${palette.grey20}`,
                borderRadius: FIELD_RADIUS,
                padding: '8px 16px',
                marginBottom: '8px',
              })}`}
            >
              <RecipientIdentity user={user} />
              <Button
                kind="minimal"
                onClick={() => handleRemove(user.id)}
                disabled={disabled}
                aria-label={t('remove_recipient', { name: displayName(user) })}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Invite prompt renders only under the primary (single) picker — the
          additional-recipients (multi) picker omits it. mt-xs (8px) block
          wrapper: BodyText3 is an inline span, so its top gap can't ride the
          Text directly. */}
      {showInvite && (
        <div className="mt-xs">
          <Text.BodyText3 color="grey70">
            {t('invite_prompt')}{' '}
            <Link
              to={MERCHANT_USERS_PATH}
              target={opensInNewTab ? '_blank' : undefined}
              rel={opensInNewTab ? 'noopener noreferrer' : undefined}
            >
              {t('invite_cta')}{' '}
              {/* The external-link icon is aria-hidden, so on the new-tab path
                  it is the ONLY signal that focus leaves the app — and that path
                  exists precisely because leaving destroys unsaved wizard state.
                  Announce it. */}
              {opensInNewTab && (
                <span className="sr-only">{t('invite_cta_opens_new_tab')}</span>
              )}
              {showInviteIcon && (
                <i className="fa fa-external-link" aria-hidden="true" />
              )}
            </Link>
          </Text.BodyText3>
        </div>
      )}
    </div>
  )
}

export default RecipientPicker
